import '../App.css';

import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
} from "@chatscope/chat-ui-kit-react";
import { useState, useEffect, useRef } from 'react';
import { useChatClient } from './useChatClient';
import { Modal, Toast, usePlib, compressImageToBase64, getFingerprintSetting } from './Components';
import { MessageViewer, ChatHeader, ChatFooter } from './MessageViewer';

function Chat({ orderId, token, serverUrl }) {
  const [connected, setConnected] = useState(false);

  // 是否已经连接上客服
  const [open, setOpen] = useState(true);

  // 订单信息
  const [order, setOrder] = useState({ id: orderId, status: 'WAITING', amount: 0 });

  // 聊天消息列表
  const [messages, setMessages] = useState({ serial: -1, list: [] });
  const messagesRef = useRef({ messages });


  // ‘我已付款’ 二次确认
  const [paidConfirm, setPaidConfirm] = useState(false)

  // 支付成功提示
  const [paidSuccess, setPaidSuccess] = useState(false)

  // 提示
  const [toastMessage, setToastMessage] = useState(null);

  // 图片check库
  const { plib } = usePlib();

  useEffect(() => {
    messagesRef.current = { messages };
  }, [messages]);

  // 重置聊天数据
  const resetMessages = async () => {
    const list = await client.messages()
    let serial = -1
    if (list && list.length) {
      serial = list.at(-1).serial ?? 0
    }
    console.info(`重置聊天数据 serial=${serial} length=${list.length}`)
    setMessages({ serial: serial, list: list ?? [] })
  }

  // 收到聊天消息
  const recvMessage = (data) => {
    console.info(`收到聊天消息`, data)
    const { messages } = messagesRef.current;

    if (data.serial !== messages.serial + 1) {
      console.error(`聊天消息serial没有同步, 重新初始化: receivd serial=${data.serial}, expected serial=${messages.serial + 1}`)
      resetMessages()
    } else {
      appendMessage(data)
    }
  }

  // 记录新的聊天消息
  const appendMessage = (data) => {
    setMessages(prev => {
      return {
        serial: prev.serial + 1,
        list: [...prev.list, data]
      }
    });
  }

  const onHeartbeat = () => {
  }

  const client = useChatClient({
    orderId,
    token,
    serverUrl,
    onMessage: recvMessage,
    onHeartbeat,
  });

  const start = async () => {
    const data = await client.start()
    setOpen(data.open)
    if (data.order) {
      setOrder(data.order)
    }

    return data
  }

  // 定时重连
  useEffect(() => {
    const interval = setInterval(async () => {
      // 如果还没有连接，不重试
      if (!connected) {
        return
      }
      if (open) {
        clearInterval(interval)
      } else {
        const data = await start()
        if (data.retcode === 119) {
          clearInterval(interval)
        }
      }
    }, 5_000)

    return () => clearInterval(interval)
  }, [connected, open])

  // 定时查询状态
  useEffect(() => {
    const interval = setInterval(() => {
      if (order.status === 'SUCCESS' || order.status === 'FAILED') {
        clearInterval(interval)
        return
      }

      const { messages } = messagesRef.current;
      const count = messages.list.filter(v => v.direction === 'incoming').length
      if (count == 0) {
        // 一定有来自客服的消息，如果没有就是还没有初始化好
        return
      }

      client.status(count)
        .then((data) => {
          if (data.retcode === 119) {
            clearInterval(interval)
            return
          }
          if (data.status) {
            setOrder(v => { return { ...v, status: data.status } })
          }
          if (data.sync) {
            resetMessages()
          }
        })
        .catch((err) => {
          console.log(err.message);
        });
    }, 5000);
    return () => clearInterval(interval);
  }, [order.status]);

  useEffect(() => {
    const func = async () => {
      const data = await start()
      setConnected(true)
      if (data.open) {
        resetMessages()
      }
    }
    func()

    // 获取指纹
    getFingerprintSetting(
      (fingerprint, ipLocation) => {
        client.fingerprint(fingerprint, ipLocation)
      },
      (message) => {
        client.report(message)
      })
  }, []);

  // 点击我已付款
  const handlePaid = () => {
    if (order.status === 'AUDIT') {
      return;
    }
    client.paid()
      .then(() => {
        setOrder({ ...order, status: 'AUDIT' });
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  // 发送文本消息
  const sendMessage = async (text) => {
    if (!text || !client) {
      return;
    }

    const message = await client.sendMessage(text, 'text')
    appendMessage(message)
  };

  // 发送图片消息
  const handleImageSelect = async (image) => {
    console.info("选择图片")
    let check = null
    plib.loadAsync(image)
      .then(r => check = r)
      .finally(async () => {
        const compressedFile = await compressImageToBase64(image, 800, 0.8);
        const message = await client.sendMessage(compressedFile.base64, 'image', check)
        message.url = compressedFile.base64
        appendMessage(message)
      })
  }

  const handleBankCardCopy = (label) => {
    setToastMessage('已复制')
    sendMessage(`我已复制了: ${label}`)
  }

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Toast
        message={toastMessage}
        visible={toastMessage !== null}
        onClose={() => setToastMessage(null)}
      />
      <Modal
        visible={paidConfirm}
        onClose={() => setPaidConfirm(false)}
        onConfirm={() => {
          handlePaid()
          setPaidConfirm(false)
          setPaidSuccess(true)
        }}
        message="确认已付款?"
      />
      <Modal
        visible={!open}
        message="正在连接通道，请稍等！"
      />
      <Modal
        visible={paidSuccess}
        onConfirm={() => setPaidSuccess(false)}
        message="提交成功"
      />
      <MainContainer style={{ height: "100%" }}>
        <ChatContainer>
          {ChatHeader(order.id, order.amount, order.status, () => {
            setPaidConfirm(true)
          })}
          {MessageViewer({
            messages: messages.list,
            fetchImage: client.image,
            handleBankCardCopy: handleBankCardCopy,
            marginBottom: order.status !== 'WAITING' ? "50px" : '90px'
          })}
        </ChatContainer>
        <ChatFooter
          disabled={!open || !connected}
          status={order.status}
          onSend={sendMessage}
          onImageSelect={handleImageSelect}
        />
      </MainContainer>
    </div>
  );
}

export default Chat;
